import { adminApi } from './api-client';
import {
  toFhirOrganization,
  toFhirOrganizationFromLegacy,
  mergeOrganizationSettings,
} from './scrips-adapter';
import type { Organization } from './fhir/types';
import type {
  ScripsOrganizationDto,
  ScripsOrganizationDataContract,
  ScripsOrganizationSettingsDto,
} from './scrips-dto';

/**
 * Organization service — every call returns FHIR `Organization` to the UI.
 * Underlying transport uses the real .NET endpoints.
 *
 * Endpoint citations:
 *   GET  /api/Organization                 — OrganizationController.cs:57
 *   GET  /api/Organization/{id}            — OrganizationController.cs:92
 *   GET  /api/v1/Organization/{id}         — OrganizationV1Controller.cs:66
 *   POST /api/v1/Organization/list         — OrganizationV1Controller.cs:160
 *   GET  /api/v1/OrganizationSettings/{id} — OrganizationSettingsController.cs:73
 *
 * The v1 list endpoint takes a body filter (`ListOrganizationV1Query`). Exact
 * field names are verified on first call in dev; we pass a minimal object.
 */

export interface ListOrganizationsQuery {
  /** Free-text search (matches Name, NameArabic). */
  q?: string;
  /** 'Active' | 'Inactive' to filter by IsActive. */
  status?: 'Active' | 'Inactive';
  /** 1-indexed page number (optional). */
  pageNumber?: number;
  /** Page size (optional). */
  pageSize?: number;
}

export async function listOrganizations(
  params: ListOrganizationsQuery = {},
  opts: { signal?: AbortSignal } = {},
): Promise<Organization[]> {
  const body = {
    ...(params.q ? { SearchQuery: params.q } : {}),
    ...(params.status ? { Status: params.status } : {}),
    ...(params.pageNumber ? { PageNumber: params.pageNumber } : {}),
    ...(params.pageSize ? { PageSize: params.pageSize } : {}),
  };
  const dtos = await adminApi.post<ScripsOrganizationDto[]>(
    '/api/v1/Organization/list',
    body,
    { signal: opts.signal },
  );
  return dtos.map(toFhirOrganization);
}

export async function getOrganizationV1(id: string): Promise<Organization> {
  const dto = await adminApi.get<ScripsOrganizationDto>(`/api/v1/Organization/${id}`);
  return toFhirOrganization(dto);
}

export async function getOrganizationLegacy(id: string): Promise<Organization> {
  const dto = await adminApi.get<ScripsOrganizationDataContract>(`/api/Organization/${id}`);
  return toFhirOrganizationFromLegacy(dto);
}

export async function getOrganizationSettings(
  id: string,
): Promise<ScripsOrganizationSettingsDto> {
  return adminApi.get<ScripsOrganizationSettingsDto>(
    `/api/v1/OrganizationSettings/${id}`,
  );
}

/**
 * Composite helper that joins v1 Organization + OrganizationSettings into a
 * single enriched FHIR Organization. Used by detail screens.
 */
export async function getOrganizationWithSettings(id: string): Promise<Organization> {
  const [org, settings] = await Promise.all([
    getOrganizationV1(id),
    getOrganizationSettings(id).catch(() => null),
  ]);
  return settings ? mergeOrganizationSettings(org, settings) : org;
}
